import React from "react";
import {
  LayoutDashboard,
  Calendar,
  Book,
  Users,
  BrainCircuit,
  GraduationCap,
  BadgeCheck,
  LucideIcon,
  LifeBuoy,
  ListChecks,
  BarChart3,
  GitFork,
  Monitor,
  Terminal,
  LogOut,
  User,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/context/auth-context";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useToast } from "@/components/ui/use-toast";
import { ModeToggle } from "@/components/ModeToggle";
import { Link } from "react-router-dom";

interface SidebarProps {
  isExpanded: boolean;
  onToggle: () => void;
}

interface NavLink {
  label: string;
  icon: LucideIcon;
  to: string;
  activePath: string;
  roles: string[];
}

const SidebarControl = ({ isExpanded, onToggle }: SidebarProps) => {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="p-0 w-7 h-7 rounded-md hover:bg-secondary"
      onClick={onToggle}
      aria-label="Toggle Sidebar"
    >
      {isExpanded ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-arrow-left"
        >
          <path d="M12 19V5M19 12H5" />
        </svg>
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-arrow-right"
        >
          <path d="M5 12h14" />
          <path d="m12 5 7 7-7 7" />
        </svg>
      )}
    </Button>
  );
};

const SidebarSection = ({
  children,
  title,
}: {
  children: React.ReactNode;
  title?: string;
}) => {
  return (
    <div className="pb-4">
      {title ? (
        <div className="text-[#004161] px-3 py-2 text-sm font-section font-semibold">{title}</div>
      ) : null}
      <ul className="space-y-1">{children}</ul>
    </div>
  );
};

const SidebarLink = ({
  label,
  icon: Icon,
  to,
  activePath,
  isExpanded,
}: {
  label: string;
  icon: LucideIcon;
  to: string;
  activePath: string;
  isExpanded: boolean;
}) => {
  const location = useLocation();
  const isActive = activePath === "/" 
    ? location.pathname === "/"
    : location.pathname.startsWith(activePath);

  return (
    <li>
      <Link
        to={to}
        className={cn(
          "group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-all duration-200",
          isActive
            ? "bg-[#0f8f8c] text-white font-semibold"
            : "text-[#004161] hover:bg-[#0f8f8c]/20 hover:text-[#0f8f8c]"
        )}
      >
        <Icon className={cn(
          "h-4 w-4 transition-colors",
          isActive ? "text-white" : "text-[#0f8f8c] group-hover:text-[#0f8f8c]"
        )} />
        {isExpanded ? <span className="font-section">{label}</span> : null}
      </Link>
    </li>
  );
};

const AppSidebar = ({ isExpanded, onToggle }: SidebarProps) => {
  const location = useLocation();
  const { profile, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/auth/login");
      toast({
        title: "Logged out successfully.",
        description: "See you again soon!",
      });
    } catch (error: any) {
      console.error("Sign out failed:", error);
      toast({
        variant: "destructive",
        title: "Error logging out",
        description: "Please try again.",
      });
    }
  };

  const mainLinks: NavLink[] = [
    {
      label: "Dashboard",
      icon: LayoutDashboard,
      to: "/dashboard",
      activePath: "/dashboard",
      roles: ["user", "staff", "admin", "tech"],
    },
    {
      label: "Schedule",
      icon: Calendar,
      to: "/schedule",
      activePath: "/schedule",
      roles: ["user", "staff", "admin", "tech"],
    },
    {
      label: "Courses",
      icon: Book,
      to: "/courses",
      activePath: "/courses",
      roles: ["user", "staff", "admin", "tech"],
    },
    {
      label: "Certificates",
      icon: GraduationCap,
      to: "/certificates",
      activePath: "/certificates",
      roles: ["user", "staff", "admin", "tech"],
    },
    {
      label: "Analytics",
      icon: BarChart3,
      to: "/analytics",
      activePath: "/analytics",
      roles: ["user", "staff", "admin", "tech"],
    },
    {
      label: "BOSA Request",
      icon: GitFork,
      to: "/bosa-request",
      activePath: "/bosa-request",
      roles: ["user", "staff", "admin", "tech"],
    },
    {
      label: "Support",
      icon: LifeBuoy,
      to: "/support/tickets",
      activePath: "/support",
      roles: ["user", "staff", "admin", "tech"]
    },
  ];

  const adminLinks: NavLink[] = [
    {
      label: "Admin Courses",
      icon: Book,
      to: "/admin/courses",
      activePath: "/admin/courses",
      roles: ["staff", "admin"],
    },
    {
      label: "Manage Certificates",
      icon: GraduationCap,
      to: "/admin/certificates",
      activePath: "/admin/certificates",
      roles: ["staff", "admin"],
    },
    {
      label: "BOSA Requests",
      icon: GitFork,
      to: "/admin/bosa-requests",
      activePath: "/admin/bosa-requests",
      roles: ["staff", "admin"],
    },
    {
      label: "Feedback Reports",
      icon: ListChecks,
      to: "/admin/feedback",
      activePath: "/admin/feedback",
      roles: ["staff", "admin"],
    },
  ];

  const techLinks: NavLink[] = [
    {
      label: "Check In",
      icon: BadgeCheck,
      to: "/check-in",
      activePath: "/check-in",
      roles: ["tech"],
    },
    {
      label: "Manage Users",
      icon: Users,
      to: "/admin/users",
      activePath: "/admin/users",
      roles: ["tech"],
    },
    {
      label: "User Role Checker",
      icon: Users,
      to: "/admin/user-role-checker",
      activePath: "/admin/user-role-checker",
      roles: ["tech"],
    },
    {
      label: "Feature Requests",
      icon: BrainCircuit,
      to: "/tech/feature-requests",
      activePath: "/tech/feature-requests",
      roles: ["tech"],
    },
    {
      label: "System Monitor",
      icon: Monitor,
      to: "/tech/system-monitor",
      activePath: "/tech/system-monitor",
      roles: ["tech"],
    },
    {
      label: "Technical Logs",
      icon: Terminal,
      to: "/tech/technical-logs",
      activePath: "/tech/technical-logs",
      roles: ["tech"],
    },
  ];

  return (
    <div className="flex flex-col h-full bg-gradient-to-b from-white to-[#0f8f8c]/5">
      <div className="flex px-3 py-2 shrink-0 border-b border-[#0f8f8c]/20">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="p-0 w-7 h-7 rounded-md hover:bg-[#0f8f8c]/20 text-[#0f8f8c]"
          onClick={onToggle}
          aria-label="Toggle Sidebar"
        >
          {isExpanded ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-arrow-left"
            >
              <path d="M12 19V5M19 12H5" />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-arrow-right"
            >
              <path d="M5 12h14" />
              <path d="m12 5 7 7-7 7" />
            </svg>
          )}
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto py-2 px-1">
        <SidebarSection>
          {mainLinks.map((link) => {
            if (link.roles.includes(profile?.role || "user")) {
              return (
                <SidebarLink
                  key={link.label}
                  label={link.label}
                  icon={link.icon}
                  to={link.to}
                  activePath={link.activePath}
                  isExpanded={isExpanded}
                />
              );
            }
            return null;
          })}
        </SidebarSection>

        {(profile?.role === "staff" || profile?.role === "admin") && (
          <SidebarSection title="Staff">
            {adminLinks.map((link) => {
              if (link.roles.includes(profile?.role || "")) {
                return (
                  <SidebarLink
                    key={link.label}
                    label={link.label}
                    icon={link.icon}
                    to={link.to}
                    activePath={link.activePath}
                    isExpanded={isExpanded}
                  />
                );
              }
              return null;
            })}
          </SidebarSection>
        )}

        {profile?.role === "tech" && (
          <SidebarSection title="Tech">
            {techLinks.map((link) => {
              if (link.roles.includes(profile?.role || "")) {
                return (
                  <SidebarLink
                    key={link.label}
                    label={link.label}
                    icon={link.icon}
                    to={link.to}
                    activePath={link.activePath}
                    isExpanded={isExpanded}
                  />
                );
              }
              return null;
            })}
          </SidebarSection>
        )}
      </div>

      <Sheet>
        <SheetTrigger asChild>
          <div className="px-3 py-2 shrink-0 border-t border-[#0f8f8c]/20">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-3 w-full justify-start px-2 hover:bg-[#0f8f8c]/20">
                  <Avatar className="h-8 w-8 ring-2 ring-[#0f8f8c]/40">
                    <AvatarImage src={profile?.avatar_url || profile?.photo_url} />
                    <AvatarFallback className="bg-[#0f8f8c]/20 text-[#004161] font-medium">
                      {profile?.first_name?.charAt(0)}{profile?.last_name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  {isExpanded && (
                    <div className="flex flex-col items-start">
                      <span className="text-sm font-medium text-[#004161]">{profile?.first_name} {profile?.last_name}</span>
                      <span className="text-xs text-[#0f8f8c]">{profile?.email}</span>
                    </div>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-80" align="end" forceMount>
                <DropdownMenuLabel className="font-title text-[#004161] font-bold">My Account</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => navigate("/profile")} className="font-section hover:bg-[#0f8f8c]/20">
                  <User className="mr-2 h-4 w-4 text-[#0f8f8c]" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-[#0f8f8c]/20" />
                <DropdownMenuItem className="hover:bg-[#0f8f8c]/20">
                  <ModeToggle />
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-[#0f8f8c]/20" />
                <DropdownMenuItem onClick={handleSignOut} className="text-red-600 hover:text-red-700 hover:bg-red-50 font-section">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </SheetTrigger>
      </Sheet>
    </div>
  );
};

export default AppSidebar;
